import { Link } from "react-router-dom";
import { ArrowRight, Target, Eye, HeartHandshake } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, Kicker } from "@/components/Reveal";

const COUNTERS = [
    { big: "2.5 Cr+", label: "Households Reached" },
    { big: "12+", label: "Original Shows" },
    { big: "25+", label: "Distribution Partners" },
    { big: "7 Days", label: "A Week On Air" },
];

const MILESTONES = [
    { phase: "The Beginning", text: "Rongeen TV begins its journey on Bengali television, quickly earning a special place in family viewing across West Bengal, Tripura and Assam." },
    { phase: "The Trust", text: "Year after year of quality programming builds deep love and loyalty among millions of viewers — children, parents and grandparents alike." },
    { phase: "Today", text: "Rongeen TV stands as Bengal's favourite family entertainment destination — 12+ original programmes across every genre, for every member of the family." },
];

export default function About() {
    return (
        <div data-testid="about-page">
            <PageHero
                kicker="About Us"
                title="Bengal's favourite"
                accent="family entertainment powerhouse"
                sub="The story of Rongeen TV — a channel that belongs to every Bengali home."
                crumb="About Us"
            />

            <section className="bg-[#F5F5F5] py-20 sm:py-28">
                <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2">
                    <Reveal>
                        <div className="relative">
                            <img
                                src="/assets/shows/ggmt-photo.jpg"
                                alt="Gane Gane Matir Tane performance"
                                className="aspect-[4/5] w-full rounded-3xl border-4 border-white object-cover shadow-2xl"
                            />
                            <img
                                src="/assets/shows/krb-photo.jpg"
                                alt="Khude Rannabati set"
                                className="absolute -bottom-8 -right-4 w-1/2 rounded-2xl border-4 border-white object-cover shadow-xl sm:-right-8"
                            />
                        </div>
                    </Reveal>
                    <div>
                        <Reveal>
                            <Kicker light>Our Story</Kicker>
                            <h2 className="font-display mt-4 text-3xl font-black tracking-tight text-[#0F0F23] sm:text-4xl">
                                A decade of trust, one beloved channel
                            </h2>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="mt-6 space-y-4 text-sm leading-relaxed text-[#555566] sm:text-base">
                                <p>
                                    Rongeen TV began its journey on Bengali television with a simple promise — wholesome, high-quality entertainment rooted in the culture and everyday lives of Bengali families. Over the years, we earned the love and trust of millions of viewers across West Bengal, Tripura, Assam and beyond.
                                </p>
                                <p>
                                    Today, Rongeen TV is a full-fledged General Entertainment Channel. Our programming spans lifestyle shows, music and cultural programmes, blockbuster movies, prime-time entertainment and much-loved children's favourites — something for every member of the family, every hour of the day.
                                </p>
                                <p>
                                    Owned and operated by Biswas Media Solutions Private Limited, Rongeen TV reaches over 2.5 crore households across Eastern India through 25+ cable and DTH operators including Tata Play, SUN Direct, Hathway and SITI.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.2}>
                            <Link
                                to="/shows"
                                data-testid="about-explore-shows-btn"
                                className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#C8102E] px-7 py-3.5 text-sm font-bold uppercase tracking-[0.15em] text-white transition-all duration-300 hover:bg-[#8F0A20]"
                            >
                                Explore Our Shows <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
                            </Link>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="bg-[#0F0F23] py-20 sm:py-28" data-testid="counters-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <div className="grid grid-cols-2 gap-5 lg:grid-cols-4">
                        {COUNTERS.map((c, i) => (
                            <Reveal key={c.label} delay={i * 0.08}>
                                <div data-testid={`counter-${i}`} className="rounded-3xl border border-white/10 bg-[#16162B] p-7 text-center">
                                    <p className="font-display text-4xl font-black tracking-tight text-[#C8102E] sm:text-5xl">{c.big}</p>
                                    <p className="mt-2 text-[11px] font-bold uppercase tracking-[0.2em] text-[#9999BB]">{c.label}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <div className="mt-14 grid gap-5 md:grid-cols-2">
                        <Reveal>
                            <div className="gold-border-glow h-full rounded-3xl bg-[#16162B] p-8">
                                <Target size={26} className="text-[#FFA200]" />
                                <h3 className="font-display mt-5 text-xl font-black uppercase tracking-wide text-white">Our Mission</h3>
                                <p className="mt-3 text-sm leading-relaxed text-[#9999BB]">
                                    To engage, educate and entertain Bengali audiences with diverse, high-quality content that resonates with every generation.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <div className="gold-border-glow h-full rounded-3xl bg-[#16162B] p-8">
                                <Eye size={26} className="text-[#FFA200]" />
                                <h3 className="font-display mt-5 text-xl font-black uppercase tracking-wide text-white">Our Vision</h3>
                                <p className="mt-3 text-sm leading-relaxed text-[#9999BB]">
                                    To be Eastern India's most loved and most watched entertainment channel — a household name synonymous with quality Bengali content.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            <section className="bg-[#0F0F23] pb-24 sm:pb-32">
                <div className="mx-auto max-w-7xl px-4 sm:px-6">
                    <Reveal>
                        <Kicker>The Journey</Kicker>
                        <h2 className="font-display mt-4 text-3xl font-black tracking-tight text-white sm:text-4xl">Milestones</h2>
                    </Reveal>
                    <div className="mt-12 grid gap-5 md:grid-cols-3">
                        {MILESTONES.map((m, i) => (
                            <Reveal key={m.phase} delay={i * 0.12}>
                                <div data-testid={`milestone-${i}`} className="relative h-full rounded-3xl border border-white/10 bg-[#16162B] p-8">
                                    <span className="font-mono2 text-4xl font-black text-[#C8102E]/30">0{i + 1}</span>
                                    <h3 className="font-display mt-4 text-lg font-black uppercase tracking-wide text-[#FFA200]">{m.phase}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-[#9999BB]">{m.text}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.2}>
                        <div className="mt-14 flex items-start gap-4 rounded-3xl border border-[#C8102E]/30 bg-[#C8102E]/5 p-7">
                            <HeartHandshake size={26} className="mt-1 shrink-0 text-[#C8102E]" />
                            <p className="text-sm leading-relaxed text-[#C9C9DE] sm:text-base">
                                Owned and operated by <span className="font-bold text-white">Biswas Media Solutions Private Limited</span>, Kolkata — committed to content that engages, educates and entertains.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>
        </div>
    );
}
